// @ts-nocheck
import React from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  Box,
  Typography,
  Grid,
  InputLabel,
  Button,
  CircularProgress,
} from "@mui/material";
import addContractorModalStyles from "../addContractorModal.module.css";
import RequiredFieldsNote from "./_components/RequiredFieldsNote";
import { requiredLabelSx } from "./_components/requiredField";

import { addContractorStep2Action } from "../../../../store/actions/contractor";
import { ALERT_DIALOG } from "../../../../store/constants/auth";
import {
  ADD_CONTRACTOR_STEP2_FAILURE,
  ADD_CONTRACTOR_STEP2_REQUEST,
  ADD_CONTRACTOR_STEP2_SUCCESS,
  SET_CONTRACTOR_MULTIPLE_FIELDS,
} from "../../../../store/constants/contractor";
import { getAllPositions } from "../../../../store/actions/positions";
import AutocompleteDropdown from "../../../../components/Custom/AutoCompleteDropdown";
import {
  getCssVariableHandler,
  getRGBAFromRGBGivenAlphaHandler,
} from "../../../../utils/styles/getCssVariables";
import { FaInfoCircle, FaStar } from "react-icons/fa";
import { CustomInfoButton } from "../../../../components/Custom/CustomIconButton";
import { EquipmentInput } from "../../../../components/Custom/EquipmentInput";
import { infinity } from "../../../../utils/values/infinity";

// A roleExperience entry is only meaningful with a role name. Contractors who
// signed up before the July 2026 cutover carry a `{ role: null }` first entry
// (no `_id`); the Yup rule below rejects it and the step can never be saved
// ("Role is required!" under that card). Drop nameless entries on hydration.
// The backend drops them on write too (common/utils/role-experience.util.ts).
const hasRoleName = (entry) => {
  const role = entry?.role ?? entry?.positionName;
  if (typeof role !== "string") return false;
  const trimmed = role.trim().toLowerCase();
  return trimmed !== "" && trimmed !== "null" && trimmed !== "undefined";
};

// Stable identity for a selected role. Legacy entries may lack `_id`, and
// comparing `undefined === undefined` used to delete (or bump the experience
// of) every entry without one at once.
const roleKey = (entry) =>
  String(entry?._id ?? entry?.Id ?? entry?.id ?? entry?.role ?? "");

const withoutNamelessRoles = (form) =>
  form && Array.isArray(form?.roleExperience)
    ? { ...form, roleExperience: form.roleExperience.filter(hasRoleName) }
    : form;

const Option = ({
  option,
  onClick,
  props,
  hoveredPosition,
  setHoveredPosition,
}) => {
  return (
    <>
      <div
        // {...props}
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          padding: "6px 8px",
          boxSizing: "border-box",
          border: `1px solid rgb(209, 117, 182)`,
          borderRadius: `4px`,
          overflow: "visible",
          cursor: "pointer",
        }}
        className={`autocomplete-option`}
        onClick={onClick}
      >
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <p
              style={{
                color: "white",
                margin: "0",
              }}
            >
              {option.label}
            </p>
            {option?.tag?.length > 0 ? (
              <FaStar color="rgb(209, 117, 182)" />
            ) : null}
            <span
              style={{
                color: "rgb(209, 117, 182)",
              }}
            >
              {option?.tag?.slice(0, 2)?.join(", ")}
            </span>
          </div>

          <div
            style={{
              position: "relative",
            }}
          >
            <CustomInfoButton
              header={option.label}
              body={option?.positionDescription}
              equipments={option?.equipmentRequired}
            />
          </div>
        </div>
        <div>
          <p
            style={{
              margin: "0",
              opacity: "0.5",
              fontSize: "0.85rem",
              color: "rgb(255, 255, 255, 0.75)",
            }}
          >
            {option?.positionBrief}
          </p>
        </div>
      </div>
    </>
  );
};

const AddContractorFormStep2 = ({
  setCurrentStep,
  uiController,
  FormDataBox,
  isEditing,
  isViewing,
  successCallbackStep2,
}) => {
  // State variables start...
  const [initialValues, setInitialValues] = React.useState({
    roleExperience: [],
    // rolesWorked: "",
    preferredPiecesOfGear: "",
    // certificatesAccreditations: ''
  });

  const [isValuesSyncedOnce, setIsValuesSyncedOnce] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [rolesWorkedSelectedPositions, setRolesWorkedSelectedPositions] =
    React.useState([]);
  // State variables end...

  // Redux hooks start...
  const dispatch = useDispatch();
  // Redux hooks end...

  // Selectors start...
  const contractor = useSelector((store) => store.contractor);
  const positions = useSelector((store) => store.positions);
  // Selectors end...

  // Form schema...
  const formSchema = Yup.object().shape({
    roleExperience: Yup.array().of(
      Yup.object().shape({
        // `_id` is a Mongoose artifact, not user input: legacy docs and rows built
        // in-memory may not carry it. Requiring it aborted submitForm() with an
        // error that has no UI anywhere -> the Continue button looked dead.
        _id: Yup.string(),
        Id: Yup.string(),
        role: Yup.string().required("Role is required!"),
        experience: Yup.number().required("Role experience is required!"),
        preferredPieceOfGears: Yup.array()
          .of(Yup.string())
          .max(30, "You can't add more than 30 preferred pieces of gear "),
      }),
    ),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: formSchema,
    onSubmit: (values) => {
      const payload = {
        ...values,
        roleExperience: values?.roleExperience?.map((role) => {
          return {
            _id: role?._id,
            role: role?.role,
            id: role?.Id,
            preferredPieceOfGears: role?.preferredPieceOfGears,
            experience: role?.experience,
          };
        }),
        rolesWorked: values?.roleExperience
          ?.map((role) => role?.role)
          ?.join?.(","),
      };

      // NOTE (2026-07-31): the "skip the PUT when nothing changed" short-circuit
      // that used to live here was removed. It compared two Redux objects
      // (step2.form vs step2.previouslySubmittedForm) that the prefill builds
      // IDENTICAL (AddContractorModal.tsx, SETUP_EDIT_CONTRACTOR_SUCCESS) and that
      // no action updates while the user types — Formik state lives in `values`.
      // So the guard fired on every click: the step advanced, the request never
      // went out, isRoleInformationAdded stayed false and the modal reopened on the
      // same step. Skipping the call is wrong by construction anyway: this endpoint
      // does not only store roleExperience, it is what sets isRoleInformationAdded.
      // The PUT is idempotent, so always send it.
      dispatch({ type: ADD_CONTRACTOR_STEP2_REQUEST });
      dispatch(
        addContractorStep2Action({
          payload,
          contractorId: contractor?.addContractor?.step1?.data?._id,
          formData: values,
          successCallback: ({ response }) => {
            setCurrentStep((currentStep) => {
              return currentStep + 1;
            });

            dispatch({
              type: ADD_CONTRACTOR_STEP2_SUCCESS,
              payload: {
                data: response?.data?.data,
                formData: values,
              },
            });

            if (successCallbackStep2) {
              successCallbackStep2(response);
            }
          },
          failureCallback: ({ error }) => {
            dispatch({ type: ADD_CONTRACTOR_STEP2_FAILURE });
            // Without this the step failed silently (parity with step 1).
            dispatch({
              type: ALERT_DIALOG,
              payload: {
                open: true,
                title: "Error!",
                message:
                  error?.response?.data?.message ||
                  "Could not save your roles. Please try again.",
              },
            });
          },
          afterResponseCallback: () => {},
        }),
      );
    },
  });

  const {
    values,
    setValues,
    handleChange,
    handleBlur,
    touched,
    errors,
    submitForm,
    resetForm,
  } = formik;
  // Form schema...

  // Handlers start...
  const handlePositionChange = (position, positionName, action) => {
    if (action === "onChange") {
      setValues((values) => {
        return {
          ...values,
          roleExperience: [...(position || values?.roleExperience)],
        };
      });
      // setRolesWorkedSelectedPositions([...(position || rolesWorkedSelectedPositions)]);
    } else if (action === "delete") {
      setValues((values) => {
        return {
          ...values,
          roleExperience:
            values?.roleExperience?.filter?.((positionIdLocal) => {
              return roleKey(positionIdLocal) !== roleKey(position);
            }) || [],
        };
      });
    } else {
      let positionIdIdx = values?.roleExperience?.findIndex?.(
        (positionIdLocal) => {
          return roleKey(positionIdLocal) === roleKey(position);
        },
      );

      if (positionIdIdx < 0) {
        setValues((values) => {
          return {
            ...values,
            roleExperience: [...(values?.roleExperience || []), position],
          };
        });
      }
    }
  };
  // Handlers end...

  // useEffects start...
  React.useEffect(() => {
    if (contractor?.addContractor?.step2?.form) {
      const form = withoutNamelessRoles(contractor?.addContractor?.step2?.form);
      setValues(form);
      setInitialValues(form);
    }
  }, []);

  React.useEffect(() => {
    if (contractor?.addContractor?.step2?.form?.rolesWorked) {
      const selectedPositions =
        contractor?.addContractor?.step2?.form?.rolesWorked?.split?.(",");
      const rolesWorkedSelectedPositionsTemp = [];

      for (let i = 0; i < selectedPositions?.length; i++) {
        const currSelectedPosition = positions?.allPositions?.data?.find?.(
          (position) => {
            return (
              position?.positionName?.toLowerCase?.() ===
              selectedPositions?.[i]?.toLowerCase?.()
            );
          },
        );

        rolesWorkedSelectedPositionsTemp.push(currSelectedPosition);
      }

      setRolesWorkedSelectedPositions(rolesWorkedSelectedPositionsTemp);
    }
  }, [positions?.allPositions?.data]);

  React.useEffect(() => {
    dispatch({
      type: SET_CONTRACTOR_MULTIPLE_FIELDS,
      payload: {
        addContractor: {
          ...contractor?.addContractor,
          step2: {
            ...contractor?.addContractor?.step2,
            form: contractor.addContractor?.step2?.form
              ? isValuesSyncedOnce
                ? values
                : contractor.addContractor?.step2?.form
              : values,
          },
        },
      },
    });

    setIsValuesSyncedOnce(true);
  }, [values, dispatch]);

  React.useEffect(() => {
    dispatch(
      getAllPositions({
        payload: {
          params: {
            limit: infinity,
          },
        },
      }),
    );
  }, []);
  // useEffects end...

  return (
    <>
      {/* <Box className={addContractorModalStyles["formDataBox"]}> */}
      <FormDataBox>
        {/* <Grid container spacing={2}
                    sx={{
                        height: '100%',
                        overflowY: 'auto'
                    }}
                    className={'custom-scroll-design'}
                > */}
        {isViewing ? null : <RequiredFieldsNote />}
        <Grid sx={{ minHeight: "30vh" }} item xs={12}>
          <Box className={addContractorModalStyles["form-box"]}>
            {/* At least one role is a step of the onboarding checklist. */}
            {/* Red until at least one role is added. */}
            <InputLabel
              sx={isViewing || values?.roleExperience?.length ? undefined : requiredLabelSx}
            >
              List Roles Worked
            </InputLabel>
            <AutocompleteDropdown
              Option={Option}
              optionOnClick={handlePositionChange}
              options={positions?.allPositions?.data
                ?.filter?.((position) => {
                  return position?.positionName
                    ?.toLowerCase()
                    ?.includes?.(searchTerm?.toLowerCase?.());
                })
                ?.map?.((position) => {
                  return {
                    ...position,
                    Id: position?._id,
                    experience: 0,
                    value: position?._id,
                    label: position?.positionName,
                    role: position?.positionName,
                  };
                })}
              // options={[]}
              handleChangeOption={handlePositionChange}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              // value={[]}
              // value={rolesWorkedSelectedPositions?.map?.((position) => {
              //   return {
              //     ...position,
              //     value: position?._id,
              //     label: position?.positionName,
              //   };
              // })}
              value={values?.roleExperience?.map?.((position) => {
                return {
                  ...position,
                  value: position?._id,
                  label: position?.positionName || position?.role,
                };
              })}
              style={{
                borderColor: "white",
              }}
              placeholder={"Type here to select roles..."}
              // defaultId={values.position}
            />

            {values?.roleExperience?.length > 0 ? (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "0.5rem",
                  marginBottom: "0.5rem",
                }}
              >
                <Typography
                  variant="span"
                  sx={{
                    color: getCssVariableHandler({
                      variableName: "--font-rgb",
                    }),
                  }}
                >
                  Add Experience
                </Typography>

                {/* <Typography
                  variant="span"
                  sx={{
                    color: getCssVariableHandler({
                      variableName: "--primary-blue-hex",
                    }),
                  }}
                >
                  Years of experience
                </Typography> */}
              </Box>
            ) : null}

            {values?.roleExperience?.length > 0 ? (
              <Box
                sx={{
                  marginTop: "0.5rem",
                  padding: "0.5rem",
                  paddingRight: "0",
                  display: "flex",
                  flexDirection: "column",
                  border: `1px solid ${getCssVariableHandler({
                    variableName: "--font-rgb",
                  })}`,
                  borderRadius: "0.5rem",
                }}
              >
                {values?.roleExperience?.length > 0 ? (
                  <Box
                    sx={{
                      width: "100%",
                      maxHeight: "250px",
                      overflowY: "auto",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      paddingRight: "0.5rem",
                      justifyContent:
                        values?.roleExperience?.length === 0
                          ? "center"
                          : "flex-start",
                      gap: "0.5rem",
                    }}
                    className={"custom-scroll-design"}
                  >
                    {values?.roleExperience?.length === 0 ? (
                      <CircularProgress size={22} />
                    ) : (
                      values?.roleExperience?.map?.((position, positionIdx) => {
                        return (
                          <React.Fragment
                            key={roleKey(position) || positionIdx}
                          >
                            <Box
                              sx={{
                                width: "100%",
                                padding: "0.5rem",
                                border: `1px solid ${getRGBAFromRGBGivenAlphaHandler(
                                  {
                                    alpha: "0.35",
                                    rgb: getCssVariableHandler({
                                      variableName: "--font-rgb",
                                    }),
                                  },
                                )}`,
                              }}
                            >
                              <Box
                                sx={{
                                  width: "100%",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  padding: "0.25rem",
                                  border: `1px solid ${getRGBAFromRGBGivenAlphaHandler(
                                    {
                                      alpha: "0.35",
                                      rgb: getCssVariableHandler({
                                        variableName: "--font-rgb",
                                      }),
                                    },
                                  )}`,
                                  borderRadius: `0.25rem`,
                                }}
                              >
                                <Typography>{position?.role}</Typography>

                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "1rem",
                                    justifyContent: "space-between",
                                    padding: "0.25rem",
                                    border: `1px solid ${getCssVariableHandler({
                                      variableName: "--font-rgb",
                                    })}`,
                                    borderRadius: `6px`,
                                    marginRight: "10px",
                                    width: "170px",
                                  }}
                                >
                                  <Button
                                    onClick={() => {
                                      formik.setFieldValue(
                                        `roleExperience`,
                                        values?.roleExperience?.map?.(
                                          (role, roleIdx) => {
                                            if (roleIdx === positionIdx) {
                                              return {
                                                ...role,
                                                experience:
                                                  role?.experience - 1,
                                              };
                                            } else {
                                              return role;
                                            }
                                          },
                                        ),
                                      );
                                      // setFieldValue(
                                      //   `roleExperience[${positionIdx}].experience`,
                                      //   values?.roleExperience?.[positionIdx]?.experience -
                                      //     1,
                                      // );
                                    }}
                                    sx={{
                                      minWidth: "0",
                                      border: `1px solid ${getCssVariableHandler(
                                        {
                                          variableName: "--primary-blue-rgb",
                                        },
                                      )}`,
                                      borderRadius: "50%",
                                      padding: "0 0.6rem",
                                      background: "none",
                                      color: `${getCssVariableHandler({
                                        variableName: "--primary-blue-rgb",
                                      })}`,
                                      "&:hover": {
                                        background: "none",
                                      },
                                      "&:disabled": {
                                        border: `1px solid ${getRGBAFromRGBGivenAlphaHandler(
                                          {
                                            alpha: "0.6",
                                            rgb: getCssVariableHandler({
                                              variableName:
                                                "--primary-blue-rgb",
                                            }),
                                          },
                                        )}`,
                                        color: `${getRGBAFromRGBGivenAlphaHandler(
                                          {
                                            alpha: "0.6",
                                            rgb: getCssVariableHandler({
                                              variableName:
                                                "--primary-blue-rgb",
                                            }),
                                          },
                                        )}`,
                                      },
                                    }}
                                    disabled={
                                      isViewing ||
                                      values?.roleExperience?.[positionIdx]
                                        ?.experience === 0
                                    }
                                  >
                                    -
                                  </Button>

                                  <Typography
                                    sx={{
                                      textWrap: "nowrap",
                                    }}
                                  >
                                    {position?.experience}{" "}
                                    {position?.experience === 0
                                      ? ""
                                      : position?.experience === 1
                                        ? "Year"
                                        : "Years"}
                                  </Typography>

                                  <Button
                                    onClick={() => {
                                      formik.setFieldValue(
                                        `roleExperience`,
                                        values?.roleExperience?.map?.(
                                          (role, roleIdx) => {
                                            if (roleIdx === positionIdx) {
                                              return {
                                                ...role,
                                                experience:
                                                  role?.experience + 1,
                                              };
                                            } else {
                                              return role;
                                            }
                                          },
                                        ),
                                      );
                                    }}
                                    sx={{
                                      minWidth: "0",
                                      border: `1px solid ${getCssVariableHandler(
                                        {
                                          variableName: "--primary-blue-rgb",
                                        },
                                      )}`,
                                      borderRadius: "50%",
                                      padding: "0 0.6rem",
                                      background: "none",
                                      color: `${getCssVariableHandler({
                                        variableName: "--primary-blue-rgb",
                                      })}`,
                                      "&:hover": {
                                        background: "none",
                                      },
                                      "&:disabled": {
                                        border: `1px solid ${getRGBAFromRGBGivenAlphaHandler(
                                          {
                                            alpha: "0.6",
                                            rgb: getCssVariableHandler({
                                              variableName:
                                                "--primary-blue-rgb",
                                            }),
                                          },
                                        )}`,
                                        color: `${getRGBAFromRGBGivenAlphaHandler(
                                          {
                                            alpha: "0.6",
                                            rgb: getCssVariableHandler({
                                              variableName:
                                                "--primary-blue-rgb",
                                            }),
                                          },
                                        )}`,
                                      },
                                    }}
                                    disabled={isViewing}
                                  >
                                    +
                                  </Button>
                                </Box>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "flex-start",
                                  alignItems: "flex-start",
                                  flexDirection: "column",
                                  width: "100%",
                                }}
                              >
                                <Typography>
                                  Preferred Pieces Of Gear
                                </Typography>
                                <EquipmentInput
                                  values={position?.preferredPieceOfGears || []}
                                  options={
                                    position?.equipmentRequired ||
                                    positions?.allPositions?.data?.find(
                                      (p) =>
                                        p?._id === position?.Id ||
                                        p?._id === position?.id,
                                    )?.equipmentRequired ||
                                    []
                                  }
                                  setFieldValue={(value) =>
                                    setValues((prev) => ({
                                      ...prev,
                                      roleExperience: prev?.roleExperience?.map(
                                        (item, idx) => {
                                          if (idx === positionIdx) {
                                            return {
                                              ...item,
                                              preferredPieceOfGears: value,
                                            };
                                          } else {
                                            return item;
                                          }
                                        },
                                      ),
                                    }))
                                  }
                                  handleBlur={handleBlur}
                                  readOnly={Boolean(isViewing)}
                                />
                                {touched.roleExperience?.[positionIdx]
                                  ?.preferredPieceOfGears &&
                                  errors.roleExperience?.[positionIdx]
                                    ?.preferredPieceOfGears && (
                                    <span
                                      className={addContractorModalStyles.error}
                                    >
                                      {
                                        errors.roleExperience?.[positionIdx]
                                          ?.preferredPieceOfGears
                                      }
                                    </span>
                                  )}

                                {/* `role` and `experience` are not typed into an
                                    input (autocomplete + -/+ buttons), so `touched`
                                    never gets set for them and their errors had no
                                    UI at all — the submit just aborted in silence.
                                    Gate on submitCount so they surface on Continue. */}
                                {formik.submitCount > 0 &&
                                  (errors.roleExperience?.[positionIdx]?.role ||
                                    errors.roleExperience?.[positionIdx]
                                      ?.experience) && (
                                    <span
                                      className={addContractorModalStyles.error}
                                    >
                                      {errors.roleExperience?.[positionIdx]
                                        ?.role ||
                                        errors.roleExperience?.[positionIdx]
                                          ?.experience}
                                    </span>
                                  )}
                              </Box>
                            </Box>
                          </React.Fragment>
                        );
                      })
                    )}
                  </Box>
                ) : null}
              </Box>
            ) : null}
          </Box>
        </Grid>
        {/* <Grid item xs={12}> */}
        {/*   <Box className={addContractorModalStyles["form-box"]}> */}
        {/*     <InputLabel>Preferred pieces of gear</InputLabel> */}
        {/*     <CustomTextFiled */}
        {/*       fullWidth */}
        {/*       placeholder="Enter gear here (comma separated)..." */}
        {/*       name="preferredPiecesOfGear" */}
        {/*       onChange={handleChange} */}
        {/*       onBlur={handleBlur} */}
        {/*       value={values.preferredPiecesOfGear} */}
        {/*       multiline={true} */}
        {/*       rows={3} */}
        {/*       sx={{ */}
        {/*         height: "fit-content !important", */}
        {/*       }} */}
        {/*       inputProps={{ */}
        {/*         readOnly: Boolean(isViewing), */}
        {/*       }} */}
        {/*     /> */}
        {/*     {touched.preferredPiecesOfGear && errors.preferredPiecesOfGear && ( */}
        {/*       <span className={addContractorModalStyles.error}> */}
        {/*         {errors.preferredPiecesOfGear} */}
        {/*       </span> */}
        {/*     )} */}
        {/*   </Box> */}
        {/* </Grid> */}

        {/* </Grid> */}
      </FormDataBox>
      {/* </Box> */}

      {uiController?.showBottomBtnBox ? (
        <Box
          className="bottom-btn-box"
          sx={{
            width: "100%",
            alignSelf: "flex-end",
            marginTop: "2rem",
          }}
        >
          <Button
            className="btn-black"
            sx={{
              width: "50%",
              height: "4.25rem",
              textTransform: "none !important",
            }}
            onClick={() => {
              // addContractorsDispatch({
              // type: 'RESET'
              // });
              // resetForm();
              setCurrentStep((currentStep) => {
                return currentStep - 1;
              });
            }}
            // onClick={onClose}
          >
            <Typography className="btn-text">Back</Typography>
          </Button>

          <Button
            className="btn-pink"
            sx={{
              width: "50%",
              height: "4.25rem",
              textTransform: "none !important",
              "&:disabled": {
                background: "#d85ad8 !important",
              },
            }}
            onClick={() => {
              if (isViewing) {
                setCurrentStep((currentStep) => {
                  return currentStep + 1;
                });
                return;
              }
              submitForm();
            }}
            disabled={
              contractor?.addContractor?.step2?.submitFormRequestLoading
            }
            // onClick={onClose}
          >
            {contractor?.addContractor?.step2?.submitFormRequestLoading ? (
              <CircularProgress size={22} />
            ) : (
              <Typography className="btn-text">
                {isViewing ? "Continue" : "Save and Continue"}
              </Typography>
            )}
          </Button>
        </Box>
      ) : null}
    </>
  );
};

AddContractorFormStep2.propTypes = {
  currentStep: PropTypes.number,
  setCurrentStep: PropTypes.func,
  uiController: PropTypes.object,
  isEditing: PropTypes.bool.isRequired,
  isViewing: PropTypes.bool.isRequired,
  successCallbackStep2: PropTypes.func,
};

export default AddContractorFormStep2;